import { ref } from "vue";
import { getOrganisations } from "@/modules/organisation/infrastructure/adapters/organisationService";
import { useAppToast } from "@/shared/composables/useAppToast";
import { logger } from "@/infrastructure/services/LoggerService";
const PAGE_SIZE = 50;
// Organisations API enforces PageSize 1-100; fetch at the maximum for the see-all browser.
const ALL_RESULTS_PAGE_SIZE = 100;
/**
 * Organisation-picker data source for the donation detail tab's edit pickers. Owns the option list,
 * loading flag, total count (drives the "see all results" footer) and the legal-name/acronym label
 * formatting. Mirrors {@link useSubscriptionOptions}; the current selection is seeded from a known
 * label rather than a by-id fetch.
 */
export function useOrganisationOptions() {
    const { toastError } = useAppToast();
    const organisations = ref([]);
    const options = ref([]);
    const loading = ref(false);
    const totalCount = ref(0);
    let loaded = false;
    function toOption(o) {
        const label = o.mainInformation.legalName?.trim() || o.mainInformation.acronym?.trim() || o.id;
        return { value: o.id, label };
    }
    function prepend(option) {
        if (option.value && !options.value.some((o) => o.value === option.value)) {
            options.value = [option, ...options.value];
        }
    }
    function seedSelected(id, label) {
        if (!id || options.value.some((o) => o.value === id))
            return;
        options.value = [{ value: id, label: label?.trim() || id }, ...options.value];
    }
    async function load(params = {}) {
        if (loaded && !params.force) {
            seedSelected(params.ensureSelectedId, params.ensureSelectedLabel);
            return;
        }
        loading.value = true;
        try {
            const res = await getOrganisations({ page: 1, pageSize: PAGE_SIZE });
            organisations.value = res.result;
            totalCount.value = res.totalItems;
            options.value = res.result.map(toOption);
            seedSelected(params.ensureSelectedId, params.ensureSelectedLabel);
            loaded = true;
        }
        catch (err) {
            logger.error("useOrganisationOptions: failed to load organisations", err);
            toastError("organisationList.loadError");
        }
        finally {
            loading.value = false;
        }
    }
    function search(query) {
        const q = query.trim().toLowerCase();
        const all = organisations.value.map(toOption);
        options.value = !q ? all : all.filter((o) => o.label.toLowerCase().includes(q));
    }
    /** Fetches a larger slice for the "see all results" browser, client-filtered by query. */
    async function fetchAll(query) {
        const res = await getOrganisations({ page: 1, pageSize: ALL_RESULTS_PAGE_SIZE });
        const q = query.trim().toLowerCase();
        const all = res.result.map(toOption);
        return !q ? all : all.filter((o) => o.label.toLowerCase().includes(q));
    }
    return { options, loading, totalCount, load, search, fetchAll, prepend };
}
